import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import { AddAccountButton } from "../components/AddAccountButton";
import { createAccount } from "../data/api";
import { Avatar } from "../components/Avatar";
import { HealthBadge } from "../components/HealthDot";
import type { Account } from "../data/types";

/** Accounts the CE team supports. Sorted worst-health first, because the list
 *  exists to surface the accounts that need attention, not to be alphabetical. */
const HEALTH_ORDER = { critical: 0, risk: 1, healthy: 2 };

export function Accounts() {
  const accounts = [...(useLoaderData() as Account[])].sort((a, b) => HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health]);
  const revalidator = useRevalidator();

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-display-xs font-semibold text-gray-900">Accounts</h1>
        <AddAccountButton
          onCreated={async (input) => {
            await createAccount(input);
            revalidator.revalidate();
          }}
        />
      </header>

      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg)]">
        <div className="grid grid-cols-[minmax(0,1fr)_130px_120px_140px_80px] gap-3 border-b border-[var(--border)] bg-gray-100 px-4 py-2 text-xs font-medium text-[var(--text-2)]">
          <span>Account</span>
          <span>Health</span>
          <span>Plan</span>
          <span>CE lead</span>
          <span className="text-right">Open</span>
        </div>
        {accounts.map((a) => (
          <Link
            key={a.id}
            to={`/accounts/${a.id}`}
            className="grid grid-cols-[minmax(0,1fr)_130px_120px_140px_80px] items-center gap-3 border-b border-[var(--border)] px-4 py-3 text-sm transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:focus-ring"
          >
            <span className="min-w-0">
              <span className="block truncate font-medium">{a.name}</span>
              <span className="block truncate text-xs text-[var(--text-2)]">{a.emailDomain}</span>
            </span>
            <HealthBadge health={a.health} />
            <span className="text-[var(--text-2)]">{a.plan}</span>
            <span className="flex items-center gap-2">
              <Avatar owner={a.lead} size="sm" />
              {a.lead.name}
            </span>
            <span className="text-right font-medium">{a.openCount}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
